import { Close as CloseIcon, Edit as EditIcon, SwapVert } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import React, { ChangeEvent, useEffect, useState } from 'react';
import useDebounce from '../../hooks/useDebounce';
import {
  Coupon,
  CouponStatus,
  DiscountType,
  DurationUnit,
  EnaraPlans,
} from '../../interfaces/eligibility';
import { CommonService } from '../../services/common';
import eligibilityService from '../../services/eligibility';
import { formatDateStringToMMDDYYYY, formatDateStringToYYYYMMDD } from '../../util/dateFormat';
import { CollapsedChipsList } from '../common/CollapsedChipsList';
import { CustomTable } from '../common/CustomTable';
import { EligibilityAllEntities } from '../eligibility/eligibility.types';
import './coupons.css';

const DEFAULT_PAGE_SIZE = 50;

const DEFAULT_COUPON = {
  id: 0,
  code: '',
  value: 0,
  duration: { value: null, unit: DurationUnit.Lifetime },
  discountType: DiscountType.Percentage,
  validFrom: new Date().toISOString(),
  validTo: new Date().toISOString(),
  status: CouponStatus.Active,
  clinics: [],
  memberships: [],
};

const CouponManagement = () => {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [currentCoupon, setCurrentCoupon] = useState<Coupon | null>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [hoveredCoupon, setHoveredCoupon] = useState<Coupon | null>(null);
  const [clinics, setClinics] = useState<{ id: number; name: string }[]>([]);
  const [memberships, setMemberships] = useState<EnaraPlans[]>([]);
  const [filters, setFilters] = useState<{
    code?: string;
    status?: CouponStatus;
    clinicId?: number;
    validFrom?: string | null;
    validTo?: string | null;
  }>({
    validFrom: null,
    validTo: null,
  });
  const [pagination, setPagination] = useState<{
    currentPage: number;
    perPage: number;
    totalCount: number;
  }>({
    currentPage: 0,
    perPage: DEFAULT_PAGE_SIZE,
    totalCount: 0,
  });

  const [formErrors, setFormErrors] = useState<{
    code?: string;
    value?: string;
    durationValue?: string;
    validFrom?: string;
    validTo?: string;
    clinics?: string;
    memberships?: string;
  }>({});

  const validateCoupon = (coupon: Coupon) => {
    const errors: typeof formErrors = {};

    if (!coupon.code || coupon.code.trim() === '') {
      errors.code = 'Coupon code is required';
    }
    if (!coupon.value || isNaN(Number(coupon.value)) || Number(coupon.value) <= 0) {
      errors.value = 'Value must be greater than 0';
    }
    if (
      coupon.duration.unit !== DurationUnit.Lifetime &&
      (!coupon.duration.value ||
        isNaN(Number(coupon.duration.value)) ||
        Number(coupon.duration.value) <= 0)
    ) {
      errors.durationValue = 'Duration value is required and must be greater than 0';
    }
    if (!coupon.validFrom || coupon.validFrom === '') {
      errors.validFrom = 'Valid From date is required';
    }
    if (!coupon.clinics || coupon.clinics.length === 0) {
      errors.clinics = 'At least one clinic is required';
    }
    if (!coupon.memberships || coupon.memberships.length === 0) {
      errors.memberships = 'At least one membership is required';
    }

    if (
      coupon.validFrom &&
      coupon.validTo &&
      new Date(coupon.validTo).getTime() < new Date(coupon.validFrom).getTime()
    ) {
      errors.validTo = 'Valid To date must be after Valid From date';
    }

    return errors;
  };

  const handleOpenCreateDialog = () => {
    setCurrentCoupon(DEFAULT_COUPON);
    setIsEditing(false);
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleOpenEditDialog = (coupon: Coupon) => {
    setCurrentCoupon({ ...coupon });
    setIsEditing(true);
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentCoupon(null);
    setFormErrors({});
  };

  const handleSaveCoupon = async () => {
    if (!currentCoupon) {
      return;
    }
    const errors = validateCoupon(currentCoupon);
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      setIsSaving(false);
      return;
    }

    setIsSaving(true);
    if (isEditing) {
      const updatedCoupon = await eligibilityService.updateCoupon(currentCoupon.id, {
        ...currentCoupon,
        membershipIds: currentCoupon.memberships.map((membership) => membership.id),
        clinicIds: currentCoupon.clinics.map((clinic) => clinic.id),
      });

      if (updatedCoupon) {
        setCoupons(
          coupons?.map((coupon) =>
            coupon.id === currentCoupon.id ? { ...currentCoupon, ...updatedCoupon } : coupon,
          ),
        );

        setCurrentCoupon(updatedCoupon);
      }
    } else {
      const newCoupon = await eligibilityService.createCoupon({
        ...currentCoupon,
        membershipIds: currentCoupon.memberships.map((membership) => membership.id),
        clinicIds: currentCoupon.clinics.map((clinic) => clinic.id),
      });

      if (newCoupon) {
        setCoupons([...coupons, { ...currentCoupon, ...newCoupon }]);
      }
    }

    setCurrentCoupon(DEFAULT_COUPON);
    handleCloseDialog();
    setIsSaving(false);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!currentCoupon) {
      return;
    }

    const { name, value } = e.target;
    setCurrentCoupon({ ...currentCoupon, [name]: value });
    setFormErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleDurationChange = (field: 'value' | 'unit', value: string | null) => {
    if (!currentCoupon) {
      return;
    }

    setCurrentCoupon((prevCoupon) => {
      if (!prevCoupon) {
        return prevCoupon;
      }
      const newDuration = {
        ...prevCoupon.duration,
        [field]: field === 'value' ? (value === '' ? null : Number(value)) : value,
      };
      return {
        ...prevCoupon,
        duration: newDuration,
      };
    });
    if (field === 'value') {
      setFormErrors((prev) => ({ ...prev, durationValue: undefined }));
    }
  };

  const handleDateChange = (field: 'validFrom' | 'validTo', date: string | null) => {
    if (!currentCoupon || (field === 'validFrom' && !date)) {
      return;
    }

    setCurrentCoupon({ ...currentCoupon, [field]: date });
    setFormErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handlePageChange = async (page: number) => {
    setPagination((prev) => ({
      ...prev,
      currentPage: page,
    }));

    await fetchCoupons(filters, page);
  };

  const handleFilterChange = (name: string, value: string | number | Date | null) => {
    const updatedFilters = {
      ...filters,
      [name]: value,
    };
    setFilters(updatedFilters);
    debouncedHandleFilterChange(updatedFilters);
  };

  const fetchCoupons = async (
    filters: {
      code?: string;
      status?: CouponStatus;
      clinicId?: number;
      validFrom?: string | null;
      validTo?: string | null;
    },
    page?: number,
  ) => {
    setLoading(true);
    const response = await eligibilityService.getCoupons({
      page: page || pagination?.currentPage,
      perPage: pagination?.perPage,
      filters: Object.fromEntries(Object.entries(filters).filter(([_, value]) => !!value)),
    });

    setCoupons(response.coupons);
    setPagination(response.pagination);
    setLoading(false);
  };

  const debouncedHandleFilterChange = useDebounce(fetchCoupons);

  useEffect(() => {
    (async () => {
      await fetchCoupons(filters);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const clinics = await CommonService.getClinics();
      const memberships =
        (
          await eligibilityService.listData<EnaraPlans>({
            entity: EligibilityAllEntities.EnaraPlans,
          })
        )?.entities || [];

      setClinics(clinics);
      setMemberships(memberships);
    })();
  }, []);

  return (
    <Box className='tab-section' marginTop={0}>
      <Box display='flex' justifyContent='space-between' alignItems='center' marginBottom={3}>
        <Typography variant='subtitle1'>Coupon Management</Typography>
        <Button
          variant='contained'
          color='primary'
          onClick={handleOpenCreateDialog}
          id='coupons-header-new-item'>
          Create Coupon
        </Button>
      </Box>

      <Box display='flex' gap={3} sx={{ mt: 1, alignItems: 'center', mb: 3 }}>
        <TextField
          fullWidth
          label='Coupon Code'
          value={filters.code}
          onChange={(e) => handleFilterChange('code', e.target.value)}
          variant='outlined'
          size='small'
        />

        <Autocomplete
          fullWidth
          size='small'
          options={clinics}
          getOptionLabel={(option) => option.name}
          value={clinics?.find((c) => c.id === Number(filters.clinicId))}
          onChange={(e, newValue) => handleFilterChange('clinicId', newValue?.id || '')}
          renderInput={(params) => <TextField {...params} label='Clinic' variant='outlined' />}
        />

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label='Valid From'
            value={filters.validFrom}
            onChange={(date) => {
              handleFilterChange('validFrom', date ? formatDateStringToYYYYMMDD(date) : null);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                size='small'
                variant='outlined'
                sx={{
                  '& .MuiInputBase-root': {
                    height: '40px',
                  },
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {filters.validFrom && (
                        <IconButton
                          size='small'
                          onClick={() => handleFilterChange('validFrom', null)}
                          aria-label='Clear date'
                          tabIndex={-1}>
                          <span style={{ fontSize: 18, fontWeight: 'bold', color: '#888' }}>×</span>
                        </IconButton>
                      )}
                      {params.InputProps?.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </LocalizationProvider>

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label='Valid To'
            value={filters.validTo}
            onChange={(date) => {
              handleFilterChange('validTo', date ? formatDateStringToYYYYMMDD(date) : null);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                size='small'
                variant='outlined'
                sx={{
                  '& .MuiInputBase-root': {
                    height: '40px',
                  },
                }}
              />
            )}
          />
        </LocalizationProvider>
      </Box>

      <CustomTable
        columns={[
          { key: 'code', label: 'Code', width: '130px', minWidth: '130px' },
          { key: 'value', label: 'Value', width: '30px', minWidth: '30px' },
          { key: 'type', label: 'Type', width: '80px', minWidth: '100px' },
          { key: 'duration', label: 'Duration', width: '70px', minWidth: '70px' },
          { key: 'clinics', label: 'Clinics', width: '100px', minWidth: '100px' },
          { key: 'memberships', label: 'Memberships', width: '100px', minWidth: '100px' },
          { key: 'validFrom', label: 'Valid From', width: '100px', minWidth: '100px' },
          { key: 'validTo', label: 'Valid To', width: '100px', minWidth: '100px' },
          { key: 'status', label: 'Status', width: '80px', minWidth: '80px' },
          { key: 'actions', label: 'Actions', width: '30px', minWidth: '30px' },
        ]}
        defaultText='No coupons available'
        key={'coupons-table'}
        loading={loading}
        rows={coupons.map((coupon) => {
          return {
            action: (
              <IconButton size='small'>
                <SwapVert />
              </IconButton>
            ),
            code: <Typography fontWeight='bold'>{coupon.code}</Typography>,
            value: (
              <Typography>
                {coupon.discountType === DiscountType.Percentage
                  ? `-${coupon.value}%`
                  : `-$${coupon.value}`}
              </Typography>
            ),
            type: (
              <Chip size='small' label={coupon.discountType} color='primary' variant='outlined' />
            ),
            clinics: <CollapsedChipsList entities={coupon?.clinics || []} />,
            memberships: (
              <CollapsedChipsList
                entities={
                  coupon?.memberships.map((membership) => ({
                    name: membership.membershipName,
                    id: membership.id,
                  })) || []
                }
              />
            ),
            duration: (
              <Box
                onMouseEnter={() => setHoveredCoupon(coupon)}
                onMouseLeave={() => setHoveredCoupon(null)}>
                <Chip
                  size='small'
                  label={
                    coupon.duration.unit === DurationUnit.Lifetime
                      ? 'Lifetime'
                      : `${coupon.duration.value} ${
                          coupon.duration.unit === DurationUnit.Monthly ? 'months' : 'years'
                        }`
                  }
                />
                {hoveredCoupon?.id === coupon.id && (
                  <Box
                    sx={{
                      position: 'absolute',
                      zIndex: 100,
                      backgroundColor: 'white',
                      padding: '10px',
                      borderRadius: '4px',
                      boxShadow: '0 0 5px rgba(0,0,0,0.2)',
                      marginTop: '5px',
                    }}>
                    <Typography variant='body2'>
                      <strong>Unit:</strong> {coupon.duration.unit}
                    </Typography>
                    {coupon.duration.value && (
                      <Typography variant='body2'>
                        <strong>Value:</strong> {coupon.duration.value}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            ),
            validFrom: formatDateStringToMMDDYYYY(coupon.validFrom),
            validTo: formatDateStringToMMDDYYYY(coupon.validTo) || '-',
            status: (
              <Chip
                size='small'
                label={
                  <Box display='flex' alignItems='center'>
                    <Typography sx={{ marginRight: '5px' }}>
                      {!coupon.validTo || new Date(coupon.validTo).getTime() >= Date.now()
                        ? CouponStatus.Active
                        : CouponStatus.Inactive}
                    </Typography>
                  </Box>
                }
                color={
                  !coupon.validTo || new Date(coupon.validTo).getTime() >= Date.now()
                    ? 'success'
                    : 'default'
                }
              />
            ),
            actions: (
              <Box display='flex'>
                <IconButton
                  size='small'
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenEditDialog(coupon);
                  }}>
                  <EditIcon fontSize='small' />
                </IconButton>
              </Box>
            ),
          };
        })}
        pagination={{
          rowsPerPageOptions: [DEFAULT_PAGE_SIZE],
          rowPerPage: pagination?.perPage || DEFAULT_PAGE_SIZE,
          page: pagination?.currentPage || 0,
          totalCount: pagination?.totalCount || 0,
          onPageChange: handlePageChange,
        }}
      />

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth='md' fullWidth>
        <DialogTitle>
          {isEditing ? 'Edit Coupon' : 'Create New Coupon'}
          <IconButton
            aria-label='close'
            onClick={handleCloseDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {currentCoupon && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label='Coupon Code'
                name='code'
                value={currentCoupon.code}
                onChange={handleChange}
                fullWidth
                margin='normal'
                required
                error={!!formErrors.code}
                helperText={formErrors.code}
              />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl fullWidth margin='normal'>
                  <InputLabel>Discount Type</InputLabel>
                  <Select
                    name='discountType'
                    value={currentCoupon.discountType}
                    onChange={(e) =>
                      setCurrentCoupon({
                        ...currentCoupon,
                        discountType: e.target.value as DiscountType,
                      })
                    }
                    label='Discount Type'>
                    <MenuItem value={DiscountType.Percentage}>Percentage</MenuItem>
                    <MenuItem value={DiscountType.Amount}>Fixed Amount</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  label='Value'
                  name='value'
                  type='number'
                  value={currentCoupon.value}
                  onChange={handleChange}
                  fullWidth
                  required
                  margin='normal'
                  error={!!formErrors.value}
                  helperText={formErrors.value}
                />
                <FormControl fullWidth margin='normal'>
                  <InputLabel>Duration</InputLabel>
                  <Select
                    name='duration'
                    required
                    value={currentCoupon.duration.unit}
                    onChange={(e) => {
                      if (e.target.value === DurationUnit.Lifetime) {
                        setCurrentCoupon({
                          ...currentCoupon,
                          duration: {
                            ...currentCoupon.duration,
                            value: null,
                          },
                        });
                      }

                      setCurrentCoupon({
                        ...currentCoupon,
                        duration: {
                          ...currentCoupon.duration,
                          unit: e.target.value as DurationUnit,
                        },
                      });
                    }}
                    label='Duration'>
                    <MenuItem value={DurationUnit.Lifetime}>Lifetime</MenuItem>
                    <MenuItem value={DurationUnit.Monthly}>Monthly</MenuItem>
                    <MenuItem value={DurationUnit.Yearly}>Yearly</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                {currentCoupon.duration.unit !== DurationUnit.Lifetime && (
                  <TextField
                    label='Duration Value'
                    type='number'
                    required
                    value={currentCoupon.duration.value || ''}
                    onChange={(e) => handleDurationChange('value', e.target.value)}
                    fullWidth
                    margin='normal'
                    error={!!formErrors.durationValue}
                    helperText={formErrors.durationValue}
                  />
                )}
              </Box>

              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <DatePicker
                    label='Valid From'
                    value={currentCoupon.validFrom}
                    onChange={(date) => handleDateChange('validFrom', date)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        fullWidth
                        margin='normal'
                        error={!!formErrors.validFrom}
                        helperText={formErrors.validFrom}
                      />
                    )}
                  />
                  <DatePicker
                    label='Valid To'
                    value={currentCoupon.validTo}
                    onChange={(date) => handleDateChange('validTo', date)}
                    renderInput={(params) => <TextField {...params} fullWidth margin='normal' />}
                  />
                </Box>
              </LocalizationProvider>

              <Box>
                <Typography variant='subtitle1'>Applicable Clinics</Typography>
                <Autocomplete
                  multiple
                  options={clinics}
                  getOptionLabel={(option) => option.name}
                  value={clinics.filter((clinic) => {
                    return !!currentCoupon.clinics?.find((c) => c.id === clinic.id);
                  })}
                  onChange={(_, value) => {
                    setCurrentCoupon({
                      ...currentCoupon,
                      clinics: value,
                    });
                    setFormErrors((prev) => ({ ...prev, clinics: undefined }));
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip label={option.name} {...getTagProps({ index })} key={option.id} />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      variant='outlined'
                      label='Select Clinics'
                      placeholder='Clinics'
                      margin='normal'
                      fullWidth
                      error={!!formErrors.clinics}
                      helperText={formErrors.clinics}
                    />
                  )}
                  sx={{ mb: 2 }}
                />
              </Box>

              <Box>
                <Typography variant='subtitle1'>Applicable Memberships</Typography>
                <Autocomplete
                  multiple
                  options={memberships}
                  getOptionLabel={(option) => option.name}
                  value={memberships.filter((plan) =>
                    currentCoupon.memberships?.find((membership) => membership.id === plan.id),
                  )}
                  onChange={(_, value) => {
                    setCurrentCoupon({
                      ...currentCoupon,
                      memberships: value.map((plan) => ({
                        id: plan.id,
                        membershipName: plan.name,
                      })),
                    });
                    setFormErrors((prev) => ({ ...prev, memberships: undefined }));
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip label={option.name} {...getTagProps({ index })} key={option.id} />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      variant='outlined'
                      label='Select Memberships'
                      placeholder='Memberships'
                      margin='normal'
                      fullWidth
                      error={!!formErrors.memberships}
                      helperText={formErrors.memberships}
                    />
                  )}
                  sx={{ mb: 2 }}
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSaveCoupon}
            variant='contained'
            color='primary'
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={20} /> : null}>
            {isSaving ? 'Saving…' : isEditing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CouponManagement as React.FC;
